
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader, Card, Table, Thead, Tbody, Tr, Th, Td, Button } from '../../components/UI';
import { User, Mail, Shield, Check, AlertCircle, Send, ArrowUpRight } from '../../components/Icons';
import { usersData } from '../../data/users';
import { invoiceData } from '../../data/invoices';
import { BrandLogo } from '../../components/BrandLogo';
import { useTranslation } from '../../contexts/LanguageContext';
import { ChannelType } from '../../types';

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  const user = usersData.find(u => u.id === userId);
  
  if (!user) {
    return <div className="p-10">User not found</div>;
  }

  // Filter invoices for this user
  const userInvoices = invoiceData.filter(inv => inv.assignedTo === userId);

  // Format currency
  const formatSwissCurrency = (val: number) => {
    return new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val) + ' CHF';
  };

  const handleEdit = () => {
    navigate('/settings/users', { state: { editUser: user } });
  };

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="p-6 space-y-8 max-w-[1400px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={user.name}
        breadcrumbs={[{ label: 'Settings' }, { label: 'Users' }, { label: user.name }]}
        onBack={() => navigate('/settings/users')}
        actions={
          <Button variant="outline" size="sm" onClick={handleEdit}>Edit Profile</Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Column */}
        <div className="space-y-6">
           <Card className="p-8 flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full bg-brand-light dark:bg-[#262626] flex items-center justify-center text-2xl font-bold text-brand-dark dark:text-white mb-4 border-4 border-white dark:border-[#111111] shadow-lg">
                 {user.avatarInitials}
              </div>
              <h2 className="text-xl font-bold text-brand-dark dark:text-white">{user.name}</h2>
              <div className="flex items-center gap-2 mt-1 text-gray-500 dark:text-gray-400">
                 <Mail size={12} />
                 <span className="text-xs font-medium">{user.email}</span>
              </div>
              
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                 <span className="px-3 py-1 rounded-full bg-brand-light dark:bg-[#262626] text-brand-dark dark:text-white text-[10px] font-bold uppercase tracking-widest flex items-center gap-2">
                    <Shield size={10} /> {user.role}
                 </span>
                 <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 ${
                    user.status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600'
                 }`}>
                    {user.status}
                 </span>
              </div>
           </Card>

           <Card className="p-6">
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-4">Authorized Channels</h3>
              <div className="grid grid-cols-2 gap-3">
                 {user.authorizedChannels.map(channel => (
                    <div key={channel} className="flex items-center gap-3 p-3 border border-gray-100 dark:border-[#333] rounded-[6px] bg-gray-50/50 dark:bg-[#1A1A1A]">
                       <BrandLogo type={channel} size={24} />
                       <span className="text-[10px] font-bold text-brand-dark dark:text-white uppercase tracking-wider">{channel}</span>
                    </div>
                 ))}
              </div>
           </Card>
        </div>

        {/* Workload / Invoices Column */}
        <div className="lg:col-span-2 space-y-6">
           <Card className="p-0 overflow-hidden border-t-4 border-t-brand-medium">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-[#333] flex justify-between items-center bg-white dark:bg-[#161616]">
                 <h3 className="text-sm font-bold text-brand-dark dark:text-white uppercase tracking-widest">Assigned Invoices</h3>
                 <span className="text-[10px] font-bold bg-brand-light dark:bg-[#262626] px-2 py-1 rounded text-brand-dark dark:text-white">{userInvoices.length} Total</span>
              </div>
              
              <Table>
                <Thead>
                   <Tr isHeader>
                      <Th className="align-top">
                        <div className="flex flex-col gap-2 pb-2">
                          <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Invoice #</span>
                          <input 
                            className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                            placeholder="Filter..."
                            onChange={(e) => handleColumnFilterChange('number', e.target.value)}
                          />
                        </div>
                      </Th>
                      <Th className="align-top">
                        <div className="flex flex-col gap-2 pb-2">
                          <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Customer</span>
                          <input 
                            className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                            placeholder="Filter..."
                            onChange={(e) => handleColumnFilterChange('customer', e.target.value)}
                          />
                        </div>
                      </Th>
                      <Th className="align-top">
                        <div className="flex flex-col gap-2 pb-2">
                          <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Amount</span>
                          <input 
                            className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                            placeholder="Filter..."
                            onChange={(e) => handleColumnFilterChange('net', e.target.value)}
                          />
                        </div>
                      </Th>
                      <Th className="align-top">
                        <div className="flex flex-col gap-2 pb-2">
                          <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Channel</span>
                          <select 
                            className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-1 focus:outline-none focus:border-brand-medium transition-colors cursor-pointer text-gray-600 dark:text-gray-300"
                            onChange={(e) => handleColumnFilterChange('channel', e.target.value)}
                          >
                            <option value="">All</option>
                            {Object.values(ChannelType).map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                      </Th>
                      <Th className="align-top">
                        <div className="flex flex-col gap-2 pb-2">
                          <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Status</span>
                          <select 
                            className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-1 focus:outline-none focus:border-brand-medium transition-colors cursor-pointer text-gray-600 dark:text-gray-300"
                            onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                          >
                            <option value="">All</option>
                            <option value="completed">Completed</option>
                            <option value="failed">Failed</option>
                            <option value="rejected">Rejected</option>
                            <option value="sendable">Draft</option>
                          </select>
                        </div>
                      </Th>
                      <Th className="text-right align-top">
                         <div className="flex flex-col gap-2 pb-2 items-end h-full">
                            <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Action</span>
                            <div className="h-8"></div>
                         </div>
                      </Th>
                   </Tr>
                </Thead>
                <Tbody>
                   {userInvoices.length > 0 ? userInvoices.map(inv => (
                      <Tr key={inv.id}>
                         <Td>
                            <span className="font-bold text-brand-dark dark:text-white">{inv.number}</span>
                            <div className="text-[9px] text-gray-400">{inv.date}</div>
                         </Td>
                         <Td className="text-sm text-gray-600 dark:text-gray-300">{inv.customer}</Td>
                         <Td className="text-sm font-medium">{formatSwissCurrency(inv.net)}</Td>
                         <Td>
                            <div className="flex items-center gap-2">
                               <BrandLogo type={inv.channel} size={18} />
                               <span className="text-[9px] font-bold uppercase text-gray-500">{inv.channel}</span>
                            </div>
                         </Td>
                         <Td>
                            {inv.status === 'completed' ? (
                               <span className="text-green-600 text-[10px] font-bold uppercase flex items-center gap-1"><Check size={10} strokeWidth={4} /> Sent</span>
                            ) : (inv.status === 'failed' || inv.status === 'rejected') ? (
                               <span className="text-red-600 text-[10px] font-bold uppercase flex items-center gap-1"><AlertCircle size={10} strokeWidth={4} /> Failed</span>
                            ) : (
                               <span className="text-gray-400 text-[10px] font-bold uppercase">Draft</span>
                            )}
                         </Td>
                         <Td className="text-right">
                            <button className="text-brand-medium hover:text-brand-dark transition-colors">
                               <ArrowUpRight size={16} />
                            </button>
                         </Td>
                      </Tr>
                   )) : (
                      <Tr>
                         <Td colSpan={6} className="text-center py-8 text-gray-400 text-xs uppercase tracking-widest font-bold">No assigned invoices</Td>
                      </Tr>
                   )}
                </Tbody>
              </Table>
           </Card>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
